import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from "lucide-react";
import clsx from "clsx";

export type AlertTone = "info" | "success" | "warning" | "danger";

export type AlertBoxProps = {
  title?: string;
  children?: React.ReactNode;
  tone?: AlertTone;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
};

const toneStyles: Record<AlertTone, { bg: string; border: string; icon: React.ReactNode; iconColor: string }> = {
  info: {
    bg: "bg-[var(--color-extended-blue-50)]/10",
    border: "border-[var(--color-extended-blue-50)]/30",
    icon: <Info size={18} />,
    iconColor: "text-[var(--color-extended-blue-50)]",
  },
  success: {
    bg: "bg-[var(--color-success-500)]/10",
    border: "border-[var(--color-success-500)]/30",
    icon: <CheckCircle size={18} />,
    iconColor: "text-[var(--color-success-500)]",
  },
  warning: {
    bg: "bg-[var(--color-warning-500)]/15",
    border: "border-[var(--color-warning-500)]/40",
    icon: <AlertTriangle size={18} />,
    iconColor: "text-[var(--color-warning-500)]",
  },
  danger: {
    bg: "bg-[var(--color-danger-500)]/10",
    border: "border-[var(--color-danger-500)]/30",
    icon: <AlertCircle size={18} />,
    iconColor: "text-[var(--color-danger-500)]",
  },
};

export function AlertBox({ title, children, tone = "info", onDismiss, icon, actions, className }: AlertBoxProps) {
  const s = toneStyles[tone];
  return (
    <div
      role="alert"
      className={clsx(
        "flex items-start gap-3 px-3 py-2.5 rounded-[var(--radius-md)] border",
        s.bg,
        s.border,
        className,
      )}
    >
      <span className={clsx("shrink-0 mt-0.5", s.iconColor)}>{icon ?? s.icon}</span>
      <div className="flex-1 min-w-0">
        {title && <div className="text-sm font-medium text-[var(--color-text-primary)]">{title}</div>}
        {children && <div className="text-sm text-[var(--color-text-secondary)] mt-0.5">{children}</div>}
        {actions && <div className="mt-2 flex gap-2">{actions}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
